import { useMutation, useQuery } from "@apollo/react-hooks";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import queryString from "query-string";
import React, { useEffect, useMemo, useState } from "react";
import casLogo from "../../../../../app/assets/images/logo_cas_full.svg";
import { FASTRACK_USER_SIGNUP_MUTATION } from "../../../graphql/mutations";
import { INIT_FASTRACK_QUERY } from "../../../graphql/queries";
import { transformCheckoutError } from "../../../modules/checkout";
import { sendSignupEventToAnalytics } from "../../../utils/analytics";
import airbrake from "../../../utils/errors";
import {
  getRecaptchaToken,
  RECAPTCHA_FASTRACK_SIGNUP
} from "../../../utils/recaptcha-v3";
import FastrackPaymentForm from "../FastrackPaymentForm";
import FastrackSuitePlan from "../FastrackSuitePlan";
import {
  Container,
  FlashError,
  HomeLink,
  NameInput,
  NameLabel,
  PaymentContent,
  PaymentSection,
  PlanContent,
  PlanSection,
  TrialHeading
} from "./styled/bundle";

function Bundle({ bundleKey }) {
  const {
    name: nameUrlParam = "",
    email = "",
    promo = "",
    mls_code: mlsCode
  } = useMemo(() => queryString.parse(window.location.search), []);
  const [name, setName] = useState(nameUrlParam);
  const [errorMsg, setErrorMsg] = useState(null);
  const [promoCodeInputValue, setPromoCodeInputValue] = useState(
    promo.toLowerCase()
  );
  const [promoCode, setPromoCode] = useState(promo.toLowerCase());
  const [formikBag, setFormikBag] = useState();
  const [selectedPlanKey, setSelectedPlanKey] = useState(bundleKey);

  // redirect to `/pricing` if we don't have MLS Code temporarily until we have mls-picker fallback state built
  useEffect(() => {
    if (!mlsCode || !["suite", "annual_suite"].includes(bundleKey)) {
      window.location.href = `${window.location.origin}/pricing${
        Boolean(promo) ? `?promo=${promo}` : ""
      }`;
    }
  }, []);

  const { loading, data } = useQuery(INIT_FASTRACK_QUERY, {
    variables: { code: mlsCode }
  });

  const hasHomebeat = get(data, "mls[0].hasHomebeat", false);
  const selectedPlan =
    get(data, "mls[0].bundles", []).find(
      (bundle) => bundle.key === selectedPlanKey
    ) || {};
  const suiteMonthly =
    get(data, "mls[0].bundles", []).find((bundle) => bundle.key === "suite") ||
    {};
  const suiteYearly =
    get(data, "mls[0].bundles", []).find(
      (bundle) => bundle.key === "annual_suite"
    ) || {};
  const foundDiscount =
    (selectedPlan.discounts || []).find(
      (discount) => discount.code === promoCode
    ) || {};
  const finalPrice =
    foundDiscount.amount > 0
      ? selectedPlan.price - foundDiscount.amount
      : selectedPlan.price;
  const isFreePlan = finalPrice <= 0;

  useEffect(() => {
    if (data && isEmpty(selectedPlan)) {
      // we couldn't find the plan or mls they were looking for, so we redirect to pricing passing the promocode along
      window.location.href = `${window.location.origin}/pricing${
        Boolean(promo) ? `?promo=${promo}` : ""
      }`;
    }
  }, [data, selectedPlan]);

  const [signUpUserViaFastrack] = useMutation(FASTRACK_USER_SIGNUP_MUTATION, {
    onCompleted: ({ fastrackUserSignup: { isNewUser, jwt } }) => {
      sendSignupEventToAnalytics({
        sku: selectedPlanKey,
        revenue: finalPrice,
        promo
      });
      localStorage.setItem("token", jwt);
      window.location.replace(
        `${window.location.origin}/app/${isNewUser ? "thanks" : "launchpad"}`
      );
    },
    onError: (error) => {
      console.error(error);
      formikBag.setSubmitting(false);
      if (error.message.includes("credit_card")) {
        const transformedErrors = error.graphQLErrors.reduce(
          (errors, graphQLError) => {
            const parsedErrors = JSON.parse(
              graphQLError.message
            ).map((parsedError) => transformCheckoutError(parsedError));
            return errors.concat(parsedErrors);
          },
          []
        );
        setErrorMsg(transformedErrors);
      } else {
        airbrake.notify({ error });
        setErrorMsg("An error occurred.");
      }
    }
  });

  async function handleSubmit({
    formValues,
    formikBag: formikBagFns,
    braintreePayload
  }) {
    setFormikBag(formikBagFns);
    const recaptchaToken = await getRecaptchaToken(RECAPTCHA_FASTRACK_SIGNUP);

    signUpUserViaFastrack({
      variables: {
        user: {
          email: formValues.email,
          name,
          mls_id: data.mls[0].id
        },
        sku: {
          type: "Bundle",
          id: selectedPlan.id
        },
        payment_method_nonce: braintreePayload ? braintreePayload.nonce : "",
        grecaptcha_response: recaptchaToken,
        discount_id: foundDiscount ? foundDiscount.id : ""
      }
    });
  }

  if (loading) return null;
  return (
    <Container>
      <PlanSection>
        <PlanContent>
          <HomeLink href="/">
            <img src={casLogo} alt="Cloud Agent Suite" />
          </HomeLink>
          {["suite", "annual_suite"].includes(bundleKey) && (
            <FastrackSuitePlan
              price={finalPrice}
              promoCode={promoCode}
              hasHomebeat={hasHomebeat}
              suiteMonthly={suiteMonthly}
              suiteYearly={suiteYearly}
              selectedPlanKey={selectedPlanKey}
              onPlanChange={(value) => setSelectedPlanKey(value)}
            />
          )}
        </PlanContent>
      </PlanSection>
      <PaymentSection>
        <PaymentContent>
          <TrialHeading>
            {window.trial_duration > 30
              ? "Try for Free"
              : `${window.trial_duration} Day Free Trial`}
          </TrialHeading>
          <NameLabel htmlFor="name-input">Name</NameLabel>
          <NameInput
            id="name-input"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FastrackPaymentForm
            email={email}
            isFreePlan={isFreePlan}
            promoInputValue={promoCodeInputValue}
            onPromoChange={(e) =>
              setPromoCodeInputValue(e.target.value.toLowerCase())
            }
            onPromoSubmit={() => setPromoCode(promoCodeInputValue)}
            onFormSubmit={handleSubmit}
          />
          {errorMsg && (
            <FlashError data-testid="flash-error">{errorMsg}</FlashError>
          )}
        </PaymentContent>
      </PaymentSection>
    </Container>
  );
}

Bundle.propTypes = {
  bundleKey: PropTypes.oneOf(["suite", "annual_suite"])
};

export default Bundle;
